import clsx from "clsx";
import { type Ref, useCallback, useEffect, useState } from "react";
import { RatingsStats } from "./RatingsStats.tsx";
import { WatchBlock } from "../WatchBlock.tsx";
import { helpers } from "../../util/helpers.ts";
import { createMobileCrossTabHref } from "../../util/mobileCrossTabLink.ts";
import { toWorker } from "../../util/toWorker.ts";
import { ResponsivePopover } from "../ResponsivePopover.tsx";
import { PLAYER } from "../../../../../common/constants.ts";
import { crossTabEmitter } from "../../util/crossTabEmitter.ts";
import i18n from "../../util/i18n.ts";
import { formatPlayerNameFromLegacyName } from "../../util/playerNames.ts";
import { TeamAbbrevText } from "../TeamAbbrevLink.tsx";
import { PositionText } from "../../util/positionNames.tsx";

const Icon = ({
  onClick,
  pid,
  ref,
  watch,
}: {
  onClick?: () => void;
  pid: number;
  ref?: Ref<HTMLElement>;
  watch: number;
}) => {
  const label = i18n.t("ratingsStatsPopover.viewRatingsAndStats", {
    ns: "ui",
    defaultValue: "View ratings and stats",
  });

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      aria-label={label}
      className={clsx(
        "btn btn-link border-0 p-0 ratings-stats-trigger watch",
        watch === 0 ? undefined : `watch-active-${watch}`,
      )}
      data-no-row-highlight="true"
      data-ratings-stats-marker=""
      data-ratings-stats-trigger-pid={pid}
      onClick={onClick}
      type="button"
    >
      <span aria-hidden="true" className="fw-semibold small">
        ST
      </span>
    </button>
  );
};

type Props = {
  // "default" means this is the default of an uncontrolled value, similar to defaultValue in React
  // undefined means "we don't know the watch value, so get it on initial load"
  defaultWatch?: number;
  disableNameLink?: boolean;
  pid: number;
  playerHref?: string;
  season?: number;
};

export const RatingsStatsPopover = ({
  defaultWatch,
  disableNameLink,
  pid,
  playerHref,
  season,
}: Props) => {
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [player, setPlayer] = useState<{
    abbrev?: string;
    tid?: number;
    age?: number;
    jerseyNumber?: string;
    name?: string;
    ratings?: {
      pos: string;
      ovr: number;
      pot: number;
      hgt: number;
      stre: number;
      spd: number;
      endu: number;
      season: number;
    };
    stats?: {
      [key: string]: number;
    };
    pid: number;
    type?: "career" | "current" | "draft" | number;
  }>({
    pid,
  });

  const [watch, setWatch] = useState(defaultWatch ?? 0);
  useEffect(() => {
    if (defaultWatch === undefined) {
      // Need to fetch initial value
      (async () => {
        const newLocalWatch = await toWorker("main", "getPlayerWatch", pid);
        setWatch(newLocalWatch);
      })();
    } else {
      // This happens when switching to a new pid but defaultWatch is supplied
      setWatch(defaultWatch);
    }

    // Need to listen for bulk action updates
    const unbind = crossTabEmitter.on("updateWatch", async (watchByPid) => {
      const newWatch = watchByPid[pid];
      if (newWatch !== undefined) {
        setWatch(newWatch);
      }
    });
    return unbind;
  }, [defaultWatch, pid]);

  // Object.is to handle NaN
  if (!Object.is(player.pid, pid)) {
    setLoadingData(false);
    setPlayer({
      pid,
    });
  }

  const loadData = useCallback(async () => {
    const p = await toWorker("main", "ratingsStatsPopoverInfo", {
      pid,
      season,
    });
    setPlayer({
      abbrev: p.abbrev,
      tid: p.tid,
      age: p.age,
      jerseyNumber: p.jerseyNumber,
      name: p.name,
      ratings: p.ratings,
      stats: p.stats,
      pid,
      type: p.type,
    });
    setLoadingData(false);
  }, [pid, season]);

  const toggle = useCallback(() => {
    if (!loadingData) {
      loadData();
      setLoadingData(true);
    }
  }, [loadData, loadingData]);

  const { abbrev, tid, age, jerseyNumber, name, ratings, stats, type } = player;
  const loadState = name ? "loaded" : loadingData ? "loading" : "idle";

  let nameBlock = null;
  if (name) {
    const playerName = formatPlayerNameFromLegacyName(name);
    nameBlock = (
      <div className="d-flex">
        {jerseyNumber ? (
          <div className="text-body-secondary jersey-number-popover align-self-end me-1">
            {jerseyNumber}
          </div>
        ) : null}
        {disableNameLink ? (
          <b>{playerName}</b>
        ) : (
          <a
            href={
              playerHref ??
              createMobileCrossTabHref(helpers.leagueUrl(["player", pid]))
            }
            className="fw-bold text-truncate"
          >
            {playerName}
          </a>
        )}
        {ratings !== undefined ? (
          <div className="ms-1">
            <PositionText pos={ratings.pos} display="full" />
          </div>
        ) : null}
        {abbrev !== undefined && tid !== undefined && tid !== PLAYER.RETIRED ? (
          <a
            href={helpers.leagueUrl([
              "roster",
              `${abbrev}_${tid}`,
              ratings ? ratings.season : undefined,
            ])}
            className="ms-1"
          >
            <TeamAbbrevText tid={tid} abbrev={abbrev} />
          </a>
        ) : null}
        {age !== undefined ? (
          <div className="ms-1 flex-shrink-0">
            {i18n.t("ratingsStatsPopover.age", {
              ns: "ui",
              defaultValue: "{{age}} yo",
              age,
            })}
          </div>
        ) : null}
        <WatchBlock
          pid={pid}
          watch={watch}
          onChange={(newWatch) => {
            // Update locally even though we'll get a crossTabEmitter event too, for responsiveness.
            setWatch(newWatch);
          }}
        />
      </div>
    );
  }

  const id = `ratings-stats-popover-${player.pid}`;

  const modalHeader = nameBlock;
  const modalBody = (
    <div
      data-ratings-stats-content-pid={pid}
      data-ratings-stats-load-state={loadState}
      {...(loadState === "loaded"
        ? { "data-ratings-stats-player-type": String(type) }
        : {})}
    >
      <RatingsStats ratings={ratings as any} stats={stats} type={type} challengeNoRatings={false} />
    </div>
  );

  const popoverContent = (
    <div
      className="text-nowrap"
      data-ratings-stats-content-pid={pid}
      data-ratings-stats-load-state={loadState}
      {...(loadState === "loaded"
        ? { "data-ratings-stats-player-type": String(type) }
        : {})}
      style={{
        minWidth: 250,
      }}
    >
      <div className="mb-2">{nameBlock}</div>
      <RatingsStats ratings={ratings as any} stats={stats} type={type} challengeNoRatings={false} />
    </div>
  );

  const renderTarget = ({
    forwardedRef,
    onClick,
  }: {
    forwardedRef?: Ref<HTMLElement>;
    onClick?: () => void;
  }) => <Icon ref={forwardedRef} onClick={onClick} pid={pid} watch={watch} />;

  return (
    <ResponsivePopover
      id={id}
      modalHeader={modalHeader}
      modalBody={modalBody}
      popoverContent={popoverContent}
      renderTarget={renderTarget}
      toggle={toggle}
    />
  );
};
